import { useShadow, useShadowStyle } from "../wcUtil.ts"
import { isSaveTrackable } from "../../logic/fileSave.ts"
import '../base/as-spinner.ts';
import styleText from './shared-modal-styles.css?raw';

/**
 * Unsaved-changes exit warning, shared by every "leaving with unsaved changes"
 * flow: the home button, new-session, and the window-close (beforeunload) guard.
 *
 * Three phases, driven by attributes:
 *   - Waiting (`waiting`): a "finishing up" spinner shown while an in-flight
 *     configure/export completes before the close proceeds. No buttons — the
 *     close is already committed and just waiting for the app to go idle.
 *   - Phase 1 (default): [Cancel] [<verb> without saving] [Save]
 *   - Phase 2 (`saved`):  [Cancel] [<verb>]
 * Clicking Save runs the regular file save then advances to Phase 2, where the
 * user decides when to actually leave — so we never have to detect when an
 * untrackable browser download finished before navigating away.
 *
 * `mode` selects the verb: 'close' -> Close, 'home' -> Exit, 'new' -> Start new.
 * Button names map to the as-app handler:
 *   cancel-exit-app | confirm-exit-app (proceed/discard) | save-before-exit
 *
 * The Save button label depends on whether the environment's save provider is
 * trackable (see fileSave.ts `isSaveTrackable`): an untrackable browser
 * <a download> can't report when it finished, so Save just advances to phase 2
 * and the label stays a plain "Save". A trackable provider (GrabCAD's native
 * dialog) finishes in one step, so the label folds the exit verb in —
 * "Save and Exit" / "Save and Close" / "Save and Start new".
 */
const VERB: Record<string, string> = { close: 'Close', home: 'Exit', new: 'Start new' };

export class AsModalUnsavedWarning extends HTMLElement {
  static get observedAttributes() {
    return ['mode', 'saved', 'waiting'];
  }

  get verb(): string {
    return VERB[this.getAttribute('mode') ?? ''] ?? 'Exit';
  }

  /**
   * Phase-1 Save button label. Trackable providers complete the save-and-exit in
   * one click, so the verb is folded in ("Save and Exit"). Untrackable downloads
   * advance to a separate phase-2 confirm, so the label stays a plain "Save".
   * Hosts with disable-session-handlers run their own save via the delegated
   * save-and-close hook, whose completion we can't track either — always plain.
   */
  get saveLabel(): string {
    if (document.querySelector('as-app')?.hasAttribute('disable-session-handlers')) return 'Save';
    return isSaveTrackable() ? `Save and ${this.verb}` : 'Save';
  }

  get html() {
    const verb = this.verb;

    if (this.hasAttribute('waiting')) {
      // Waiting — the close is committed and we're holding it until an in-flight
      // configure/export finishes. No buttons: there's nothing to decide yet.
      return /*html*/`
        <div id="warning-panel" class="panel-content">
          <div class="row option-buttons">
            <as-spinner style="--spinner-width: 2rem;"></as-spinner>
          </div>
          <as-text dict-key="apps.general.finishing-up" class="center">Finishing up, please wait...
          </as-text>
        </div>
      `;
    }

    if (this.hasAttribute('saved')) {
      // Phase 2 — work has been saved (or a browser download was triggered); the
      // user chooses when to leave.
      return /*html*/`
        <div id="warning-panel" class="panel-content">
          <as-text dict-key="apps.general.saved-leave-when-ready">Your changes have been saved. You can leave when you are ready.
          </as-text>
          <div class="row option-buttons">
            <as-text-button name="cancel-exit-app">Cancel</as-text-button>
            <as-text-button name="confirm-exit-app">${verb}</as-text-button>
          </div>
        </div>
      `;
    }

    // Phase 1 — unsaved.
    return /*html*/`
        <div id="warning-panel" class="panel-content warning">
          <as-text dict-key="apps.general.may-lose-changes">You have unsaved changes.
          </as-text>
          <as-text dict-key="apps.general.save-before-leaving" class="center">Save your work before leaving?
          </as-text>
          <div class="row option-buttons">
            <as-text-button name="cancel-exit-app">Cancel</as-text-button>
            <as-text-button name="confirm-exit-app">${verb} without saving</as-text-button>
            <as-text-button name="save-before-exit">${this.saveLabel}</as-text-button>
          </div>
        </div>
    `
  }

  connectedCallback() {
    useShadow(this, this.html)
    useShadowStyle(this, styleText, './shared-modal-styles.css')
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.shadowRoot) {
      this.shadowRoot.innerHTML = this.html;
      useShadowStyle(this, styleText, './shared-modal-styles.css')
    }
  }
}

customElements.define("as-modal-unsaved-warning", AsModalUnsavedWarning)
